import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getAdminCenterData } from '@routes/sidebar/___shared___/model/adminCenterData.slice.ts';
import type {
    CreateVesselRequest,
    CsvUploadStateResponse,
    UpdateVesselRequest,
    Vessel,
    VesselsRequest,
    VesselsResponse,
} from '@routes/sidebar/admin-center/___shared___/api/vessels.api.ts';
import {
    createApiVessel,
    deleteApiVessel,
    downloadApiVesselsCsv,
    downloadApiVesselsExampleCsv,
    getApiVessels,
    getApiVesselsCsvUpload,
    updateApiVessel,
    uploadApiVesselsCsv,
} from '@routes/sidebar/admin-center/___shared___/api/vessels.api.ts';
import type { CommonTablePaginatedResponse } from '@routes/sidebar/admin-center/___shared___/types.ts';
import { VESSELS_STEP_TITLE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/vessels/constants.ts';
import type { VesselsRow } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/vessels/ui/table/types.ts';
import { CSV_UPLOAD_STATUS } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/constants.ts';
import { setPublishedSteps } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/model/configuration.slice.ts';
import type { TransformedSubItemForMatrices } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/types.ts';
import { downloadFromDisposition } from '@utils/download.ts';
import { extractError } from '@utils/slice.ts';
import { isNil } from 'lodash';

export interface VesselsError {
    message?: string;
    [k: string]: any;
}

export interface VesselsState {
    error: VesselsError | null;
    isCsvImportErroneous: boolean;
    isCsvImportFailed: boolean;
    uploadedCsvFilename: string;
    isDeleteVesselModalOpened: boolean;
    isActivateVesselModalOpened: boolean;
    isDeactivateVesselModalOpened: boolean;
    isUploadCsvModalOpened: boolean;
    uploadedCsvFile: File | null;
    uploadedCsvId: string | null;
    csvUploadState: CsvUploadStateResponse | null;

    transformedVesselsForMatrices: TransformedSubItemForMatrices[];

    commonVessels: VesselsResponse | null;
    vessels: Vessel[];
    pagination: CommonTablePaginatedResponse;

    updatedVessel: Vessel | null;
    selectedVessel: VesselsRow | null;
    selectedRowInitialData: VesselsRow | null;
    currentGetRequestId: string | null;

    isGetVesselsPending: boolean;
    isCreateVesselPending: boolean;
    isUpdateVesselPending: boolean;
    isDeleteVesselPending: boolean;
    isUploadCsvPending: boolean;
    isGetCsvUploadPending: boolean;
    isDownloadCsvPending: boolean;
    isGetExampleCsvPending: boolean;
}

const initialState: VesselsState = {
    error: null,
    isCsvImportErroneous: false,
    isCsvImportFailed: false,
    uploadedCsvFilename: '',
    isDeleteVesselModalOpened: false,
    isActivateVesselModalOpened: false,
    isDeactivateVesselModalOpened: false,
    isUploadCsvModalOpened: false,
    uploadedCsvFile: null,
    uploadedCsvId: null,
    csvUploadState: null,

    transformedVesselsForMatrices: [],

    commonVessels: null,
    vessels: [],
    pagination: {
        pageNumber: 0,
        pageSize: 0,
        totalPages: 0,
        totalResults: 0,
        maxPageSize: 0,
    },
    updatedVessel: null,
    selectedVessel: null,
    selectedRowInitialData: null,
    currentGetRequestId: null,

    isGetVesselsPending: false,
    isCreateVesselPending: false,
    isUpdateVesselPending: false,
    isDeleteVesselPending: false,
    isUploadCsvPending: false,
    isGetCsvUploadPending: false,
    isDownloadCsvPending: false,
    isGetExampleCsvPending: false,
};

export const getVessels = createAsyncThunk<
    VesselsResponse,
    { clientId: number; tenantAlias: string; payload: VesselsRequest },
    { rejectValue: VesselsError }
>('adminCenterVessels/getVessels', ({ clientId, tenantAlias, payload }, { rejectWithValue, dispatch }) =>
    getApiVessels(clientId, tenantAlias, payload)
        .then((res) => {
            if (res.data?.length > 0) {
                dispatch(setPublishedSteps(VESSELS_STEP_TITLE));
            }
            return res;
        })
        .catch((err) => rejectWithValue(err)),
);

export const createVessel = createAsyncThunk<
    Vessel,
    { clientId: number; tenantAlias: string; payload: CreateVesselRequest },
    { rejectValue: VesselsError }
>('adminCenterVessels/createVessel', ({ clientId, tenantAlias, payload }, { rejectWithValue }) =>
    createApiVessel(clientId, tenantAlias, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const updateVessel = createAsyncThunk<
    Vessel,
    { clientId: number; tenantAlias: string; id: number; payload: UpdateVesselRequest },
    { rejectValue: VesselsError }
>('adminCenterVessels/updateVessel', ({ clientId, tenantAlias, id, payload }, { rejectWithValue }) =>
    updateApiVessel(clientId, tenantAlias, id, payload)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const deleteVessel = createAsyncThunk<
    Vessel,
    { clientId: number; tenantAlias: string; id: number },
    { rejectValue: VesselsError }
>('adminCenterVessels/deleteVessel', ({ clientId, tenantAlias, id }, { rejectWithValue }) =>
    deleteApiVessel(clientId, tenantAlias, id)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const uploadVesselsCsv = createAsyncThunk<
    string,
    { clientId: number; tenantAlias: string; formData: FormData },
    { rejectValue: VesselsError }
>('adminCenterVessels/uploadVesselsCsv', ({ clientId, tenantAlias, formData }, { rejectWithValue }) =>
    uploadApiVesselsCsv(clientId, tenantAlias, formData)
        .then((res) => res)
        .catch((err) => rejectWithValue(err)),
);

export const getVesselsCsvUpload = createAsyncThunk<
    CsvUploadStateResponse,
    { clientId: number; tenantAlias: string; id: string },
    { rejectValue: VesselsError }
>('adminCenterVessels/getVesselsCsvUpload', ({ clientId, tenantAlias, id }, { rejectWithValue, dispatch }) =>
    getApiVesselsCsvUpload(clientId, tenantAlias, id)
        .then((res) => {
            if (res.uploadStatus === CSV_UPLOAD_STATUS.SUCCESS) {
                dispatch(setPublishedSteps(VESSELS_STEP_TITLE));
            }

            return res;
        })
        .catch((err) => rejectWithValue(err)),
);

export const downloadVesselsCsv = createAsyncThunk<
    void,
    { clientId: number; tenantAlias: string },
    { rejectValue: VesselsError }
>('adminCenterVessels/downloadVesselsCsv', ({ clientId, tenantAlias }, { rejectWithValue }) =>
    downloadApiVesselsCsv(clientId, tenantAlias)
        .then((res) => {
            downloadFromDisposition(res);
        })
        .catch((err) => rejectWithValue(err)),
);

export const downloadVesselsExampleCsv = createAsyncThunk<
    void,
    { clientId: number; tenantAlias: string },
    { rejectValue: VesselsError }
>('adminCenterVessels/downloadVesselsExampleCsv', ({ clientId, tenantAlias }, { rejectWithValue }) =>
    downloadApiVesselsExampleCsv(clientId, tenantAlias)
        .then((res) => {
            downloadFromDisposition(res);
        })
        .catch((err) => rejectWithValue(err)),
);

const vesselsSlice = createSlice({
    name: 'adminCenterVessels',
    initialState,
    reducers: {
        setIsCsvImportErroneous(state, action: PayloadAction<boolean>) {
            state.isCsvImportErroneous = action.payload;
        },
        setIsCsvImportFailed(state, action: PayloadAction<boolean>) {
            state.isCsvImportFailed = action.payload;
        },
        setUploadedCsvFilename(state, action: PayloadAction<string>) {
            state.uploadedCsvFilename = action.payload;
        },
        setIsDeleteVesselModalOpened(state, action: PayloadAction<boolean>) {
            state.isDeleteVesselModalOpened = action.payload;
        },
        setIsActivateVesselModalOpened(state, action: PayloadAction<boolean>) {
            state.isActivateVesselModalOpened = action.payload;
        },
        setIsDeactivateVesselModalOpened(state, action: PayloadAction<boolean>) {
            state.isDeactivateVesselModalOpened = action.payload;
        },
        setIsUploadCsvModalOpened(state, action: PayloadAction<boolean>) {
            state.isUploadCsvModalOpened = action.payload;
        },
        setUploadedCsvFile(state, action: PayloadAction<File | null>) {
            state.uploadedCsvFile = action.payload;
        },
        setSelectedVessel(state, action: PayloadAction<VesselsRow | null>) {
            state.selectedVessel = action.payload;
        },
        setCsvUploadState(state, action: PayloadAction<CsvUploadStateResponse | null>) {
            state.csvUploadState = action.payload;
        },
        clearVesselsState(state) {
            state.isCsvImportErroneous = false;
            state.isCsvImportFailed = false;
            state.csvUploadState = null;
            state.uploadedCsvFilename = '';
            state.uploadedCsvFile = null;
            state.uploadedCsvId = null;
        },
        setSelectedRowInitialData(state, action: PayloadAction<VesselsRow | null>) {
            state.selectedRowInitialData = action.payload;
        },
        setVessels(state, action: PayloadAction<Vessel[]>) {
            state.vessels = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getVessels.pending, (state, action) => {
            state.isGetVesselsPending = true;
            state.error = null;
            state.currentGetRequestId = action.meta.requestId;
        });
        builder.addCase(getVessels.fulfilled, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetVesselsPending = false;
            state.currentGetRequestId = null;
            const { data, pagination } = action.payload;
            state.commonVessels = action.payload;
            state.vessels = data ?? [];
            state.pagination = pagination;

            state.transformedVesselsForMatrices = data
                //  // filter out vessels without group or subtype
                .filter((v) => !isNil(v.vesselGroupId) && !isNil(v.vesselSubTypeId))
                .map((v) => ({
                    value: String(v.id),
                    label: String(v.vesselSubTypeShortCode),
                    type: String(v.vesselSubTypeId),
                    category: String(v.vesselGroupId),
                }));
        });
        builder.addCase(getVessels.rejected, (state, action) => {
            if (state.currentGetRequestId !== action.meta.requestId) return;

            state.isGetVesselsPending = false;
            state.currentGetRequestId = null;
            state.error = extractError(action);
        });

        builder.addCase(createVessel.pending, (state) => {
            state.isCreateVesselPending = true;
            state.error = null;
        });
        builder.addCase(createVessel.fulfilled, (state, action) => {
            state.isCreateVesselPending = false;
            state.updatedVessel = action.payload;
        });
        builder.addCase(createVessel.rejected, (state, action) => {
            state.isCreateVesselPending = false;
            state.error = extractError(action);
        });

        builder.addCase(updateVessel.pending, (state) => {
            state.isUpdateVesselPending = true;
            state.error = null;
        });
        builder.addCase(updateVessel.fulfilled, (state, action) => {
            state.isUpdateVesselPending = false;
            state.updatedVessel = action.payload;
        });
        builder.addCase(updateVessel.rejected, (state, action) => {
            state.isUpdateVesselPending = false;
            state.error = extractError(action);
        });

        builder.addCase(deleteVessel.pending, (state) => {
            state.isDeleteVesselPending = true;
            state.error = null;
        });
        builder.addCase(deleteVessel.fulfilled, (state, action) => {
            state.isDeleteVesselPending = false;
            state.updatedVessel = action.payload;
            state.vessels = state.vessels.filter((v) => v.id !== action.payload.id);
        });
        builder.addCase(deleteVessel.rejected, (state, action) => {
            state.isDeleteVesselPending = false;
            state.error = extractError(action);
        });
        builder.addCase(uploadVesselsCsv.pending, (state) => {
            state.isUploadCsvPending = true;
            state.error = null;
            state.isCsvImportFailed = false;
            state.uploadedCsvId = null;
            state.csvUploadState = null;
        });
        builder.addCase(uploadVesselsCsv.fulfilled, (state, action) => {
            state.isUploadCsvPending = false;
            state.uploadedCsvId = action.payload;
            state.uploadedCsvFile = null;
        });
        builder.addCase(uploadVesselsCsv.rejected, (state, action) => {
            state.isUploadCsvPending = false;
            state.isCsvImportFailed = true;
            state.error = extractError(action);
        });
        builder.addCase(getVesselsCsvUpload.pending, (state) => {
            state.isGetCsvUploadPending = true;
            state.error = null;
            state.isCsvImportErroneous = false;
        });

        builder.addCase(getVesselsCsvUpload.fulfilled, (state, action) => {
            state.isGetCsvUploadPending = false;
            state.csvUploadState = action.payload;

            if (action.payload.uploadStatus === CSV_UPLOAD_STATUS.SUCCESS) {
                state.uploadedCsvId = null;
            }
        });

        builder.addCase(getVesselsCsvUpload.rejected, (state, action) => {
            state.isGetCsvUploadPending = false;
            state.error = extractError(action);
        });

        builder.addCase(downloadVesselsCsv.pending, (state) => {
            state.isDownloadCsvPending = true;
            state.error = null;
        });
        builder.addCase(downloadVesselsCsv.fulfilled, (state) => {
            state.isDownloadCsvPending = false;
        });
        builder.addCase(downloadVesselsCsv.rejected, (state, action) => {
            state.isDownloadCsvPending = false;
            state.error = extractError(action);
        });

        builder.addCase(downloadVesselsExampleCsv.pending, (state) => {
            state.isGetExampleCsvPending = true;
            state.error = null;
        });
        builder.addCase(downloadVesselsExampleCsv.fulfilled, (state) => {
            state.isGetExampleCsvPending = false;
        });
        builder.addCase(downloadVesselsExampleCsv.rejected, (state, action) => {
            state.isGetExampleCsvPending = false;
            state.error = extractError(action);
        });

        builder.addCase(getAdminCenterData.fulfilled, (state, action) => {
            const { vessel } = action.payload;

            if (!vessel) return;

            const { data, pagination } = vessel;
            state.commonVessels = vessel;
            state.vessels = data ?? [];
            state.pagination = pagination;

            state.transformedVesselsForMatrices = (data ?? [])
                .filter((v) => !isNil(v.vesselGroupId) && !isNil(v.vesselSubTypeId))
                .map((v) => ({
                    value: String(v.id),
                    label: String(v.vesselSubTypeShortCode),
                    type: String(v.vesselSubTypeId),
                    category: String(v.vesselGroupId),
                }));
        });
    },
});

export const {
    setIsCsvImportErroneous,
    setIsCsvImportFailed,
    setUploadedCsvFilename,
    setIsDeleteVesselModalOpened,
    setIsActivateVesselModalOpened,
    setIsDeactivateVesselModalOpened,
    setIsUploadCsvModalOpened,
    setUploadedCsvFile,
    setSelectedVessel,
    setCsvUploadState,
    clearVesselsState,
    setSelectedRowInitialData,
    setVessels,
} = vesselsSlice.actions;

export default vesselsSlice.reducer;
